import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import { DfpTable } from 'components/molecules/Dokumenty/DfpTable';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import { EZpusobPodani, IPrikazPrechod, PrikazPrechodGet, PrikazPrechodPut } from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';

import { isIsinDlt } from '../NovyDokument';
import { getDataIsins } from './Zruseni';

interface IPrikazVytesneni {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const PrikazVytesneni: React.FC<IPrikazVytesneni> = ({ nextStep, zadostID, prikazyData }) => {
    const [form] = Form.useForm();
    const { dokumenty } = useContext(Context);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshIsins, rerender] = useState<boolean>(false);
    const [data, setData] = useState<IPrikazPrechod>();
    const [DfpTableRender, DfpUcetRender, SouborRender, displayMode] = DfpTable(
        zadostID,
        () =>
            setData(pv => {
                return {
                    ...pv,
                    ...form.getFieldsValue()
                };
            }),
        form,
        false,
        'vytesneni'
    );
    const [prechodIsins, SetPrechodIsins] = useState<{ isin?: string }[]>([
        {
            isin: undefined
        }
    ]);
    const [dataIsins, setDataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        getDataIsins(dokumenty).map(e => {
            return {
                labelcz: e.isin,
                value: e.isin
            };
        }) || []
    );

    const [selectedIsin, setSelectIsin] = useState<string>();
    const [isDLT, setIsDLT] = useState<boolean>();
    useEffect(() => {
        if (selectedIsin) {
            setIsDLT(isIsinDlt(selectedIsin, dokumenty!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    useEffect(() => {
        setTimeout(() => {
            const fieldsvals = form.getFieldsValue();
            const selectedIsins = prechodIsins.map((_e, i) => fieldsvals[`prechodISIN${i}`]);

            setDataIsins(
                getDataIsins(dokumenty)
                    .filter(e => !selectedIsins.includes(e.isin))
                    .map(e => {
                        return {
                            labelcz: e.isin,
                            value: e.isin
                        };
                    }) || []
            );
        }, 200);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshIsins]);

    useEffect(() => {
        setLoading(true);
        PrikazPrechodGet(zadostID)
            .then(resp => {
                setData(resp);
                form.setFieldsValue({
                    typzpusob: resp.typzpusob
                });
                SetPrechodIsins(resp.prechisin);
                setSelectIsin(resp.prechisin[0].isin);
            })
            .catch(() => setData({} as IPrikazPrechod))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {data &&
                prikazyData &&
                (displayMode === 'soubor' ? (
                    SouborRender()
                ) : displayMode === 'ucet' ? (
                    DfpUcetRender()
                ) : (
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            setLoading(true);
                            const dataToSend = {
                                datum: values.datum.format('YYYY-MM-DD'),
                                ...(values.datumvypis && {
                                    datumvypis: values.datumvypis.format('YYYY-MM-DD')
                                }),
                                datumzapis: values.datumzapis.format('YYYY-MM-DD'),
                                typzpusob: values.typzpusob,
                                navakcie: values.navakcie,
                                pocetcp: values.pocetcp,
                                poccphlav: values.poccphlav,
                                prechisin: prechodIsins.map((_e, i) => {
                                    return {
                                        isin: values[`prechodISIN${i}`]
                                    };
                                })
                            } as IPrikazPrechod;

                            PrikazPrechodPut(zadostID, dataToSend)
                                .then(() => nextStep())
                                .finally(() => setLoading(false));
                        }}
                        preserve={true}
                    >
                        <Title text="EMITENT" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        {prikazyData?.spolecnost.dicdph && (
                            <InputComponent
                                disabled={true}
                                initialValue={prikazyData?.spolecnost.dicdph}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dicdphSpolecnost"
                                notRequired={true}
                            />
                        )}
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.lei}
                            label="LEI_CELE"
                            name="leiSpolecnost"
                            notRequired={true}
                        />
                        {(prechodIsins || []).map((e, i) => (
                            <SelectComponent
                                key={`prechodISIN${i}`}
                                name={`prechodISIN${i}`}
                                labelParagraph={'PRIKAZ_VYTESNENI_ISIN'}
                                defaultValue={e?.isin}
                                isFormItem={true}
                                data={dataIsins}
                                labelExtra={
                                    i === prechodIsins.length - 1 || i === 0 ? (
                                        <Button
                                            id="c2PlusAltBtn"
                                            htmlType="button"
                                            type="primary"
                                            style={{
                                                display: 'inline-block',
                                                marginLeft: 20,
                                                width: 50
                                            }}
                                            defaultValue={e.isin}
                                            disabled={false}
                                            icon={i === 0 ? <PlusOutlined /> : <MinusOutlined />}
                                            onClick={() => {
                                                if (i === 0)
                                                    SetPrechodIsins(pv => [
                                                        ...pv,
                                                        {
                                                            isin: undefined
                                                        }
                                                    ]);
                                                else {
                                                    form.setFieldsValue({
                                                        [`prechodISIN${i}`]: undefined
                                                    });
                                                    SetPrechodIsins(pv => pv.filter((_e, index) => i !== index));
                                                }
                                                setTimeout(() => rerender(pv => !pv), 0);
                                            }}
                                        />
                                    ) : null
                                }
                                onChange={val => {
                                    i === 0 && setSelectIsin(val);
                                    rerender(pv => !pv);
                                }}
                            />
                        ))}
                        <>
                            <Title level={5} text="PRIKAZ_VYTESNENI_TABLE_TITLE" />
                            {DfpTableRender()}
                        </>
                        <DatePickerComponent
                            name="datumzapis"
                            initialValue={data?.datumzapis ? dayjs(data.datumzapis) : undefined}
                            //  disabledDate={current => current < dayjs()}
                            label="PRIKAZ_PREVOD_DATUM_VYTESNENI"
                        />
                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            /*  disabledDate={current =>
                                current < (form.getFieldValue('datum') || dayjs()).add(1, 'months')
                            } */
                            infoTooltipText="PRIKAZ_PREVOD_DATUM_VYTESNENI_TOOLTIP"
                            label="PRIKAZ_PREVOD_DATUM"
                            onChange={val =>
                                form.setFieldsValue({
                                    datumvypis: val.subtract(1, 'days')
                                })
                            }
                        />
                        <SelectComponent
                            name="typzpusob"
                            labelParagraph="PRIKAZ_VYTESNENI_ZPUSOB"
                            isFormItem={true}
                            data={[
                                /*        {
                                    value: EZpusobPodani.CDCP,
                                    labelcz: 'PRIKAZ_VYTESNENI_ZPUSOB_CDCP'
                                },*/
                                {
                                    value: EZpusobPodani.MD,
                                    labelcz: 'PRIKAZ_VYTESNENI_ZPUSOB_MD'
                                },
                                {
                                    value: EZpusobPodani.NEPOZADUJE,
                                    labelcz: 'PRIKAZ_VYTESNENI_ZPUSOB_NEPOZADUJE'
                                }
                            ]}
                            onChange={val => {
                                const datumVal = form.getFieldValue('datum');
                                val === EZpusobPodani.MD &&
                                    datumVal &&
                                    form.setFieldsValue({
                                        datumvypis: datumVal.subtract(1, 'days')
                                    });
                                rerender(pv => !pv);
                            }}
                            defaultValue={data?.typzpusob}
                        />
                        {form.getFieldValue('typzpusob') === EZpusobPodani.MD && (
                            <DatePickerComponent
                                name="datumvypis"
                                initialValue={data?.datumvypis ? dayjs(data.datumvypis) : undefined}
                                //  disabledDate={current => current < dayjs()}
                                label="PRIKAZ_VYTESNENI_KEDNI"
                                disabled={true}
                            />
                        )}
                        <InputNumberComponent
                            name="navakcie"
                            label="PRIKAZ_VYTESNENI_PRTIPLATENI"
                            initialValue={data?.navakcie}
                            precision={4}
                        />
                        <InputNumberComponent
                            name="pocetcp"
                            label="PRIKAZ_VYTESNENI_POCET_AKCII"
                            initialValue={data?.pocetcp}
                            precision={0}
                        />
                        <InputNumberComponent
                            name="poccphlav"
                            label="PRIKAZ_VYTESNENI_POCET_AKCII_VLASTNI"
                            initialValue={data?.poccphlav}
                            precision={0}
                        />
                        <PrilohyTable zadostID={zadostID} required={true} />
                        <Title level={5} text="PRIKAZ_VYTESNENI_PODMINKY_TITLE" />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_VYTESNENI_PODMINKY_DLT_TEXT1' : 'PRIKAZ_VYTESNENI_PODMINKY_TEXT1'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_VYTESNENI_PODMINKY_DLT_TEXT2' : 'PRIKAZ_VYTESNENI_PODMINKY_TEXT2'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_VYTESNENI_PODMINKY_DLT_TEXT3' : 'PRIKAZ_VYTESNENI_PODMINKY_TEXT3'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Row
                            style={{
                                marginTop: 26
                            }}
                        >
                            <CheckboxComponent
                                name="souhlas"
                                checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                onChange={() => setIsChecked(pv => !pv)}
                                checked={isChecked}
                            />
                        </Row>
                        <Row className="button-row">
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    disabled={!isChecked}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                ))}
        </LoadingWrapper>
    );
};

export default PrikazVytesneni;
